import { useEffect, useState } from "react";

import { useWindowSize } from "@newsela/angelou";
import { useRouter } from "next/router";

import { actionPrefixes, objectTypes, actionNames } from "@newsela/snitch";
import ButtonWithPopOutMenu from "@/components/ButtonWithPopOutMenu";
import useSearchParamsUpdate from "@/utils/useSearchParamsUpdate";
import { buttonWithPopOutMenuCss } from "./ReportFilterDropdown.style";

interface MenuOption {
  value: string;
  label: string;
}

export interface ReportFilterDropdownProps {
  /**
   *  An array of objects that should be displayed in the popup list.
   */
  menuOptions: Array<MenuOption>;

  /**
   *  The default selected option if the filter's search param is empty.
   *  Selecting the default value clears the filters URL search param.
   */
  defaultOption: MenuOption;

  /**
   *  The hidden label used for the combobox input. Required for accessibility.
   */
  label: string;

  /**
   *  The URL search parameter that this filter should update.
   */
  urlSearchParam: string;

  /**
   *  The filter's description that will be passed to the event tracking object.
   */
  eventTrackingDescription: string;

  /**
   *  Optional: Custom callback that fires when the selected option has changed
   */
  onChange?: (selectedOption: MenuOption) => void;

  /**
   *  Optional: Override the button contents. Defaults to the currently selected option.
   */
  buttonContents?: string | null;

  /**
   * Optional: Bypass updating the URL search param and dropdown selection for these values.
   */
  valuesThatShouldNotTriggerUrlUpdate?: MenuOption["value"][];

  /**
   * Optional: Ref object to the dropdown button, used for focus management.
   */
  dropdownButtonRef?: React.RefObject<HTMLButtonElement>;
}

const ReportFilterDropdown = ({
  menuOptions,
  defaultOption,
  label,
  urlSearchParam,
  eventTrackingDescription,
  onChange,
  buttonContents,
  valuesThatShouldNotTriggerUrlUpdate = [],
  dropdownButtonRef,
}: ReportFilterDropdownProps) => {
  const { query } = useRouter();
  const handleSearchParamsUpdate = useSearchParamsUpdate();

  const rawUrlSearchParamValue = query[urlSearchParam];
  const urlSearchParamValue =
    rawUrlSearchParamValue && Array.isArray(rawUrlSearchParamValue)
      ? rawUrlSearchParamValue[0]
      : rawUrlSearchParamValue;
  const defaultSelectedOption =
    menuOptions?.find((option) => option.value === urlSearchParamValue) ??
    defaultOption;

  const [selectedOption, setSelectedOption] = useState(defaultSelectedOption);

  const { width: windowWidth } = useWindowSize();
  const isMediumSize = windowWidth >= 593 && windowWidth <= 913;

  useEffect(() => {
    if (defaultSelectedOption.value !== selectedOption.value) {
      setSelectedOption(defaultSelectedOption);
    }
  }, [defaultSelectedOption?.value]);

  useEffect(() => {
    if (selectedOption === defaultSelectedOption) return;
    if (valuesThatShouldNotTriggerUrlUpdate?.includes(selectedOption.value))
      return;

    if (selectedOption && selectedOption.value !== "") {
      handleSearchParamsUpdate({
        setParams: [{ name: urlSearchParam, value: selectedOption.value }],
      });
    } else {
      handleSearchParamsUpdate({
        deleteParams: [{ name: urlSearchParam }],
      });
    }
  }, [selectedOption]);

  const streamingProps = {
    actionName: actionNames.filter,
    objectType: objectTypes.report,
    actionPrefix: actionPrefixes.initiate,
    streamProperties: {
      description: eventTrackingDescription,
      report_filters: "label selected",
    },
  };

  const handleOnSelectedOption = (optionValue: string) => {
    const foundSelectedOption = menuOptions.find(
      (option) => option.value === optionValue,
    );

    const nextSelectedOption = foundSelectedOption ?? defaultOption;

    if (
      !valuesThatShouldNotTriggerUrlUpdate?.includes(nextSelectedOption.value)
    ) {
      setSelectedOption(nextSelectedOption);
    }

    if (onChange) {
      onChange(nextSelectedOption);
    }
  };

  return (
    <ButtonWithPopOutMenu
      __cssFor={buttonWithPopOutMenuCss(isMediumSize)}
      buttonContents={buttonContents ?? selectedOption?.label}
      currentOptionValue={selectedOption?.value}
      menuOptions={menuOptions}
      onSelectedOption={handleOnSelectedOption}
      buttonRef={dropdownButtonRef}
      {...streamingProps}
      label={label}
    />
  );
};

export default ReportFilterDropdown;
